import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';
import { motion } from 'framer-motion';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isHovered, setIsHovered] = useState<string | null>(null);

  const handleSubscribe = (e: FormEvent) => {
    e.preventDefault();
    setEmail('');
  };

  const socialLinks = [
    { icon: 'ri-linkedin-line', href: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: 'ri-twitter-x-line', href: 'https://twitter.com', label: 'Twitter' },
    { icon: 'ri-instagram-line', href: 'https://instagram.com', label: 'Instagram' },
    { icon: 'ri-youtube-line', href: 'https://youtube.com', label: 'YouTube' },
  ];

  return (
    <footer className="bg-[#0a0a0a] text-white relative overflow-hidden">
      {/* Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-48 md:w-96 h-48 md:h-96 bg-[#FF6B6B]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-48 md:w-96 h-48 md:h-96 bg-[#4ECDC4]/10 rounded-full blur-3xl" />

      {/* Main Footer */}
      <div className="relative z-10 px-4 md:px-8 lg:px-16 py-12 md:py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          {/* Top Section */}
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 mb-12 md:mb-20">
            {/* Left - Brand */}
            <div>
              <Link to="/" className="inline-block mb-6 md:mb-8 cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/61ffb5e343a60f9cf4b23b262abbef4a.png"
                  alt="Kustomer"
                  className="h-8 md:h-10"
                />
              </Link>
              <p className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight mb-6 md:mb-8 max-w-md">
                Marketing ownership for
                <span className="bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] bg-clip-text text-transparent"> serious growth</span>
              </p>
              
              {/* Newsletter */}
              <form onSubmit={handleSubscribe} className="flex gap-2 md:gap-3 max-w-md">
                <div className="flex-grow relative group">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 md:px-6 py-3 md:py-4 bg-white/5 border border-white/10 rounded-full text-white text-sm md:text-base placeholder-white/40 focus:outline-none focus:border-[#4ECDC4] transition-all"
                  />
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FF6B6B]/20 to-[#4ECDC4]/20 opacity-0 group-focus-within:opacity-100 blur-xl transition-opacity -z-10" />
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] rounded-full flex-shrink-0 cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-lg md:text-xl"></i>
                </motion.button>
              </form>
            </div>

            {/* Right - Links */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
              {/* Services */}
              <div>
                <h3 className="text-xs md:text-sm font-semibold uppercase tracking-wider text-white/40 mb-4 md:mb-6">Services</h3>
                <ul className="space-y-3 md:space-y-4">
                  {[
                    'Full Marketing',
                    'Digital Marketing',
                    'Video Production',
                    'Social Media',
                    'PR & Visibility',
                    'Web Design'
                  ].map((service) => (
                    <li key={service}>
                      <Link
                        to="/services"
                        onMouseEnter={() => setIsHovered(service)}
                        onMouseLeave={() => setIsHovered(null)}
                        className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer flex items-center gap-2 py-1"
                      >
                        <motion.span
                          animate={{ x: isHovered === service ? 5 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {service}
                        </motion.span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company */}
              <div>
                <h3 className="text-xs md:text-sm font-semibold uppercase tracking-wider text-white/40 mb-4 md:mb-6">Company</h3>
                <ul className="space-y-3 md:space-y-4">
                  {[
                    { name: 'About', path: '/about' },
                    { name: 'Work', path: '/work' },
                    { name: 'Pricing', path: '/pricing' },
                    { name: 'Contact', path: '/contact' },
                  ].map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.path}
                        onMouseEnter={() => setIsHovered(link.name)}
                        onMouseLeave={() => setIsHovered(null)}
                        className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer flex items-center gap-2 py-1"
                      >
                        <motion.span
                          animate={{ x: isHovered === link.name ? 5 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {link.name}
                        </motion.span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Connect */}
              <div className="col-span-2 md:col-span-1">
                <h3 className="text-xs md:text-sm font-semibold uppercase tracking-wider text-white/40 mb-4 md:mb-6">Connect</h3>
                <div className="space-y-3 md:space-y-4">
                  <a href="mailto:info@kustomer.com.ng" className="block text-sm text-white/60 hover:text-white transition-colors cursor-pointer py-1">
                    info@kustomer.com.ng
                  </a>
                  <a href="tel:+2348145540554" className="block text-sm text-white/60 hover:text-white transition-colors cursor-pointer py-1">
                    08145540554
                  </a>
                </div>
                
                {/* Social Links */}
                <div className="flex gap-3 mt-6 md:mt-8">
                  {socialLinks.map((social) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-10 h-10 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:border-[#4ECDC4] hover:bg-[#4ECDC4]/10 transition-colors cursor-pointer"
                      aria-label={social.label}
                    >
                      <i className={`${social.icon} text-lg`}></i>
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 md:pt-10 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
            <p className="text-xs md:text-sm text-white/40 text-center md:text-left">
              © 2025 Kustomer. All rights reserved.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <a href="#" className="text-xs md:text-sm text-white/40 hover:text-white transition-colors cursor-pointer py-1">
                Privacy Policy
              </a>
              <span className="w-1 h-1 rounded-full bg-white/20 hidden md:block" />
              <a href="#" className="text-xs md:text-sm text-white/40 hover:text-white transition-colors cursor-pointer py-1">
                Terms of Service
              </a>
              <span className="w-1 h-1 rounded-full bg-white/20 hidden md:block" />
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs md:text-sm text-white/40 hover:text-[#4ECDC4] transition-colors cursor-pointer py-1"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
